import { apiFetch } from "./api";
import type { AttendanceItem } from "./attendance-api";
import type { CorrectionRequest } from "./requests-api";

/**
 * Tenant reporting endpoints (FEAT-2026-006).
 *
 * The dashboard deliberately loads from a single aggregated endpoint rather
 * than fanning out to ~15 requests across attendance/employees/sites/requests.
 */

export interface DashboardKpis {
  totalEmployees: number;
  checkedInToday: number;
  checkedOutToday: number;
  pendingRequests: number;
  exceptions: number;
  activeSites: number;
}

/** One point on the 7-day trend. Days with no records are present with zeroes. */
export interface DailyTrendPoint {
  date: string;
  day: string;
  present: number;
  exception: number;
  missed: number;
}

/** Pie slice for today. Zero-value statuses are omitted by the backend. */
export interface StatusBreakdownPoint {
  status: string;
  label: string;
  value: number;
}

/** Today's per-site snapshot, busiest first (backend caps the list). */
export interface SiteAttendancePoint {
  siteId: string;
  site: string;
  present: number;
  exception: number;
}

export interface TenantDashboard {
  kpis: DashboardKpis;
  dailyTrend: DailyTrendPoint[];
  statusBreakdown: StatusBreakdownPoint[];
  siteAttendance: SiteAttendancePoint[];
  recentActivity: AttendanceItem[];
  exceptionAlerts: AttendanceItem[];
  pendingApprovals: CorrectionRequest[];
  meta: {
    timezone: string;
    /** The tenant's current date. */
    today: string;
    /** Range the range-sensitive figures describe. */
    from: string;
    to: string;
    siteId: string | null;
    /** Chart window — may be wider than [from,to] for short selections. */
    trendFrom: string;
    trendTo: string;
  };
}

export interface TenantDashboardQuery {
  /** Single day. Wins over dateFrom/dateTo if both are supplied. */
  date?: string;
  dateFrom?: string;
  dateTo?: string;
  /** Narrow every figure to one site. */
  siteId?: string;
  /** Or to several (FEAT-2026-030). */
  siteIds?: string[];
}

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    // Lists go as `a,b` (FEAT-2026-030); an empty one is no filter.
    if (Array.isArray(v) && v.length === 0) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ── Tenant report (FEAT-2026-009) ────────────────────────────────────────────

/** Attendance rolled up by the employee's department. */
export interface DepartmentAttendancePoint {
  /** `''` = the Unassigned bucket (employees with no department). */
  departmentId: string;
  department: string;
  present: number;
  partial: number;
  exception: number;
  missed: number;
  total: number;
}

/** Request approval performance over the selected range. */
export interface ApprovalStats {
  decidedCount: number;
  /** Mean submit→decision time in minutes, or null when nothing was decided. */
  avgTurnaroundMinutes: number | null;
  /** Share (0–100) turned around within the SLA, or null. */
  withinSlaPct: number | null;
  /** Open requests older than the SLA, right now. */
  overdueCount: number;
  slaHours: number;
}

/** One day of arrival punctuality (BR-157). */
export interface LatenessPoint {
  date: string;
  /** Days with a measurable arrival — the denominator. */
  measured: number;
  late: number;
  avgLateMinutes: number;
}

export interface LatenessSummary {
  measuredDays: number;
  lateDays: number;
  totalLateMinutes: number;
  avgLateMinutes: number;
  /**
   * Null when nothing was measurable — no late threshold configured, or no
   * arrivals in range. Never render null as 0%: that reads as perfect
   * punctuality when the truth is that nothing was measured.
   */
  latePct: number | null;
}

export interface TenantReport {
  summary: { present: number; partial: number; exception: number; missed: number; total: number };
  lateness: LatenessSummary;
  latenessTrend: LatenessPoint[];
  dailyTrend: DailyTrendPoint[];
  statusBreakdown: StatusBreakdownPoint[];
  byDepartment: DepartmentAttendancePoint[];
  bySite: SiteAttendancePoint[];
  approvals: ApprovalStats;
  meta: {
    timezone: string;
    today: string;
    from: string;
    to: string;
    siteId: string | null;
    departmentId: string | null;
    trendFrom: string;
    trendTo: string;
    slaHours: number;
  };
}

export interface TenantReportQuery {
  date?: string;
  dateFrom?: string;
  dateTo?: string;
  siteId?: string;
  /** Several sites (FEAT-2026-030). */
  siteIds?: string[];
  departmentId?: string;
  /** Several departments, each with its subtree (FEAT-2026-030). */
  departmentIds?: string[];
}

export const reportsApi = {
  tenantDashboard: (q: TenantDashboardQuery = {}) =>
    apiFetch<TenantDashboard>(
      `/reports/tenant-dashboard${toQuery(q as Record<string, unknown>)}`,
    ),

  tenantReport: (q: TenantReportQuery = {}) =>
    apiFetch<TenantReport>(
      `/reports/tenant-report${toQuery(q as Record<string, unknown>)}`,
    ),
};
